import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";

import { cn } from "@/lib/utils";
import {
  dropdownChevronClassName,
  dropdownTriggerClassName,
} from "@/components/ui/dropdown-trigger-styles";

export type SelectSize = "sm" | "md";

const SelectSizeContext = React.createContext<SelectSize>("md");

type SelectProps = React.ComponentProps<typeof SelectPrimitive.Root> & {
  size?: SelectSize;
};

function Select({ size = "md", ...props }: SelectProps) {
  return (
    <SelectSizeContext.Provider value={size}>
      <SelectPrimitive.Root {...props} />
    </SelectSizeContext.Provider>
  );
}

const SelectGroup = SelectPrimitive.Group;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => {
  const size = React.useContext(SelectSizeContext);
  return (
    <SelectPrimitive.Trigger
      ref={ref}
      className={cn(
        "group flex w-full items-center justify-between gap-2 whitespace-nowrap border font-inter text-sm font-medium text-neutral-800 ring-offset-white data-[placeholder]:text-neutral-400 disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
        size === "md" ? "h-10 px-3.5 py-2.5" : "h-8 px-3 py-1.5",
        dropdownTriggerClassName,
        className
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <i
          className={cn(
            "shrink-0 text-center transition-transform duration-200 group-data-[state=open]:rotate-180",
            size === "md"
              ? "fa-solid fa-chevron-down size-4"
              : "fa-regular fa-chevron-down size-5 text-[16px] leading-5",
            dropdownChevronClassName
          )}
        />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
});
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectScrollUpButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollUpButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollUpButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollUpButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    {...props}
  >
    <i className="fa-solid fa-chevron-up h-4 w-4" />
  </SelectPrimitive.ScrollUpButton>
));
SelectScrollUpButton.displayName = SelectPrimitive.ScrollUpButton.displayName;

const SelectScrollDownButton = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.ScrollDownButton>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.ScrollDownButton>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.ScrollDownButton
    ref={ref}
    className={cn(
      "flex cursor-default items-center justify-center py-1",
      className
    )}
    {...props}
  >
    <i className="fa-solid fa-chevron-down h-4 w-4" />
  </SelectPrimitive.ScrollDownButton>
));
SelectScrollDownButton.displayName =
  SelectPrimitive.ScrollDownButton.displayName;

const SelectContent = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content>
>(({ className, children, position = "popper", ...props }, ref) => {
  const size = React.useContext(SelectSizeContext);
  const content = (
    <SelectPrimitive.Content
      ref={ref}
      className={cn(
        "relative z-[1100] max-h-[min(var(--radix-select-content-available-height,24rem),24rem)] overflow-y-auto overflow-x-hidden border border-neutral-100 bg-white transition-opacity duration-100 data-[state=open]:opacity-100 data-[state=closed]:opacity-0 origin-[--radix-select-content-transform-origin]",
        size === "md"
          ? "min-w-[8rem] rounded-md shadow-none"
          : "box-border w-[var(--radix-select-trigger-width)] min-w-[var(--radix-select-trigger-width)] rounded-lg shadow-[0px_4px_8px_-2px_rgba(16,24,40,0.1),0px_2px_4px_-2px_rgba(16,24,40,0.06)]",
        position === "popper" &&
          "data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className
      )}
      position={position}
      {...props}
    >
      <SelectScrollUpButton />
      <SelectPrimitive.Viewport
        className={cn(
          "p-1",
          position === "popper" && [
            "w-full",
            size === "md"
              ? "h-[var(--radix-select-trigger-height)] min-w-[var(--radix-select-trigger-width)]"
              : "min-w-0",
          ]
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectScrollDownButton />
    </SelectPrimitive.Content>
  );

  // Skip portal in test environment for better testability
  if (process.env.NODE_ENV === "test") {
    return content;
  }

  return <SelectPrimitive.Portal>{content}</SelectPrimitive.Portal>;
});
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Label
    ref={ref}
    className={cn("px-2 py-1.5 text-sm font-semibold", className)}
    {...props}
  />
));
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ className, children, ...props }, ref) => {
  const size = React.useContext(SelectSizeContext);
  return (
    <SelectPrimitive.Item
      ref={ref}
      className={cn(
        "relative flex w-full cursor-default select-none items-center text-sm text-neutral-700 outline-none hover:bg-neutral-50 hover:text-neutral-900 focus:bg-neutral-50 focus:text-neutral-900 data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
        size === "md"
          ? "rounded-sm py-1.5 pl-2 pr-8 font-light"
          : "h-8 rounded-none px-3 py-1.5 pr-10 font-medium",
        className
      )}
      {...props}
    >
      <span
        className={cn(
          "absolute flex items-center justify-center",
          size === "md" ? "right-2 size-3.5" : "right-3 size-5"
        )}
      >
        <SelectPrimitive.ItemIndicator>
          <i
            className={cn(
              "text-blue-500",
              size === "md"
                ? "fa-solid fa-check size-4"
                : "fa-regular fa-check size-5 text-center text-[16px] leading-5"
            )}
          />
        </SelectPrimitive.ItemIndicator>
      </span>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
});
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>(({ className, ...props }, ref) => (
  <SelectPrimitive.Separator
    ref={ref}
    className={cn("-mx-1 my-1 h-px bg-neutral-100", className)}
    {...props}
  />
));
SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
  SelectScrollUpButton,
  SelectScrollDownButton,
};
